import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useTheme } from "next-themes";
import { ChevronDown, Sun, Moon } from "lucide-react";
import { getBackendUrl } from "@/lib/backend";
import "./LoginPage.css";

const BACKEND_URL = getBackendUrl();
const DEMO_EMAIL = "admin@mahaedume.gov.in";
const DEMO_PASSWORD = "admin123";
const REMEMBER_KEY = "login_remember_email";

const LoginPage = ({ onLogin }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setTheme, resolvedTheme } = useTheme();
  const redirectTo = location.state?.from || "/executive-dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [resetEmail, setResetEmail] = useState("");

  const currentTheme = resolvedTheme || theme || "light";
  const isDark = currentTheme === "dark";

  useEffect(() => {
    try {
      const saved = localStorage.getItem(REMEMBER_KEY);
      if (saved) {
        setEmail(saved);
        setRememberMe(true);
      }
    } catch {
      // ignore storage errors
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email,
        password,
      });

      const { access_token, user } = response.data;

      localStorage.setItem("token", access_token);
      localStorage.setItem("user", JSON.stringify(user));
      axios.defaults.headers.common.Authorization = `Bearer ${access_token}`;

      if (rememberMe) {
        localStorage.setItem(REMEMBER_KEY, email);
      } else {
        localStorage.removeItem(REMEMBER_KEY);
      }

      toast.success(`Welcome back, ${user.full_name}!`);
      onLogin(user);
      navigate(redirectTo, { replace: true });
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed. Please check your credentials.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/auth/password-reset-request`, {
        email: resetEmail,
      });
      toast.success("Password reset instructions sent to your email");
      setResetMode(false);
    } catch {
      toast.error("Failed to send reset email");
    } finally {
      setLoading(false);
    }
  };

  const fillDemoCredentials = () => {
    setEmail(DEMO_EMAIL);
    setPassword(DEMO_PASSWORD);
    toast.message("Demo credentials filled");
  };

  return (
    <div className="login-page" data-testid="login-page">
      <div className="lp-shell">
        <div className="lp-mesh" aria-hidden="true" />

        <svg className="lp-wave-lines" viewBox="0 0 1600 190" fill="none" aria-hidden="true">
          <path d="M-20 108C162 18 282 162 462 86C646 8 748 152 948 74C1160 -9 1276 142 1628 42" stroke="#2563eb" strokeOpacity=".18" strokeWidth="2" />
          <path d="M-40 138C164 44 306 184 488 108C672 30 792 174 980 96C1194 8 1338 166 1644 72" stroke="#2563eb" strokeOpacity=".16" strokeWidth="2" />
          <path d="M-70 168C146 74 302 196 520 130C738 64 850 190 1042 124C1264 48 1368 190 1660 102" stroke="#14b8a6" strokeOpacity=".14" strokeWidth="2" />
        </svg>

        <header className="lp-header">
          <div className="lp-container lp-header-inner">
            <div className="lp-brand" aria-label="MH Education Login">
              <span className="lp-brand-icon" aria-hidden="true">
                <svg className="lp-large-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 21h18" />
                  <path d="M5 21V7l7-4 7 4v14" />
                  <path d="M9 21v-6h6v6" />
                  <path d="M9 10h.01M12 10h.01M15 10h.01" />
                </svg>
              </span>
              <span className="lp-brand-copy">
                <strong>MH Education</strong>
                <span>Dashboard 2025–26</span>
              </span>
            </div>

            <button
              type="button"
              className="lp-theme-toggle"
              onClick={() => setTheme(isDark ? "light" : "dark")}
              aria-label="Toggle theme"
            >
              {isDark ? <Moon className="lp-svg-icon" /> : <Sun className="lp-svg-icon" />}
              {isDark ? "Dark mode" : "Light mode"}
              <ChevronDown size={16} />
            </button>
          </div>
        </header>

        <main className="lp-main">
          <div className="lp-container lp-layout">
            <section className="lp-hero" aria-label="Login page introduction">
              <svg className="lp-map-outline" viewBox="0 0 380 420" fill="none" aria-hidden="true">
                <path d="M183 24c32 4 58 29 89 38 29 8 60-3 78 20 20 26-9 55-5 87 4 33 37 55 31 88-6 36-48 44-76 66-31 25-47 67-86 72-39 4-61-39-95-57-36-20-90-9-105-46-14-35 29-67 34-104 5-35-28-72-11-102 18-31 77-20 107-39 16-10 20-25 39-23Z" stroke="#2563eb" strokeWidth="3" />
              </svg>

              <svg className="lp-building-ghost" viewBox="0 0 520 300" fill="none" aria-hidden="true">
                <path d="M56 268h408" stroke="currentColor" strokeWidth="8" strokeLinecap="round" />
                <path d="M103 258V133h314v125" stroke="currentColor" strokeWidth="4" />
                <path d="M135 132c18-54 58-82 126-82s108 28 126 82" stroke="currentColor" strokeWidth="4" />
                <path d="M260 50V21" stroke="currentColor" strokeWidth="4" />
                <path d="M211 258v-76c0-26 20-48 49-48s49 22 49 48v76" stroke="currentColor" strokeWidth="4" />
                <path d="M131 166h40v92M349 166h40v92M88 258h344" stroke="currentColor" strokeWidth="4" />
                <path d="M69 132h382M114 104h292" stroke="currentColor" strokeWidth="4" />
              </svg>

              <div className="lp-eyebrow">Welcome to</div>
              <h1>
                Maharashtra&apos;s
                <br />
                Education Dashboard
              </h1>
              <p className="lp-hero-subtitle">
                Monitor. Analyze. Improve. Real-time insights for stronger schools and brighter futures.
              </p>
              <div className="lp-accent-line" aria-hidden="true" />

              <div className="lp-benefit-grid">
                <article className="lp-benefit-card">
                  <div className="lp-benefit-icon">
                    <svg className="lp-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 3v18h18" />
                      <path d="m19 9-5 5-4-4-3 3" />
                    </svg>
                  </div>
                  <h3>Real-Time Monitoring</h3>
                  <p>Track key indicators and school performance live.</p>
                </article>
                <article className="lp-benefit-card">
                  <div className="lp-benefit-icon">
                    <svg className="lp-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M13 2 3 14h8l-1 8 10-12h-8l1-8Z" />
                    </svg>
                  </div>
                  <h3>AI-Powered Insights</h3>
                  <p>Smart analytics to drive better decisions.</p>
                </article>
                <article className="lp-benefit-card">
                  <div className="lp-benefit-icon">
                    <svg className="lp-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
                      <path d="m9 12 2 2 4-4" />
                    </svg>
                  </div>
                  <h3>Trusted State Data</h3>
                  <p>Secure, accurate and authoritative information.</p>
                </article>
              </div>

              <div className="lp-trust-band">
                <div className="lp-trust-icon">
                  <svg className="lp-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
                    <path d="m9 12 2 2 4-4" />
                  </svg>
                </div>
                <div>
                  <b>Secure. Reliable. Trusted by Maharashtra.</b>
                  <span>Your access is protected with state-grade security.</span>
                </div>
              </div>
            </section>

            <section className="lp-login-area" aria-label="Sign in form">
              <svg className="lp-dashboard-ghost" viewBox="0 0 380 260" fill="none" aria-hidden="true">
                <rect x="28" y="24" width="318" height="212" rx="22" stroke="currentColor" strokeWidth="8" />
                <path d="M66 76h76M66 110h42M66 164v36M118 142v58M170 104v96M222 132v68" stroke="currentColor" strokeWidth="12" strokeLinecap="round" />
                <path d="M268 82a54 54 0 1 1-1 0" stroke="currentColor" strokeWidth="14" />
                <path d="M268 82v54h54" stroke="currentColor" strokeWidth="14" strokeLinecap="round" strokeLinejoin="round" />
              </svg>

              <svg className="lp-school-ghost" viewBox="0 0 420 260" fill="none" aria-hidden="true">
                <path d="M43 238h334" stroke="currentColor" strokeWidth="8" strokeLinecap="round" />
                <path d="M87 238V102h246v136" stroke="currentColor" strokeWidth="5" />
                <path d="M70 102h280L210 32 70 102Z" stroke="currentColor" strokeWidth="5" />
                <path d="M192 238v-74h36v74" stroke="currentColor" strokeWidth="5" />
                <path d="M122 140h44v42h-44zM254 140h44v42h-44z" stroke="currentColor" strokeWidth="5" />
                <path d="M190 119h40" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
                <path d="M210 32V10h44l-9 12 9 12h-44" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
                <rect x="166" y="104" width="88" height="30" rx="7" stroke="currentColor" strokeWidth="5" />
                <path d="M181 124h58" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
              </svg>

              <form className="lp-login-card" onSubmit={resetMode ? handleResetPassword : handleLogin}>
                <header>
                  <div className="lp-login-top-icon">
                    <svg className="lp-large-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="4" y="11" width="16" height="10" rx="2" />
                      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                    </svg>
                  </div>
                  <h2>{resetMode ? "Reset Password" : "Sign In"}</h2>
                  <p className="lp-subtitle">
                    {resetMode
                      ? "Enter your email to receive reset instructions"
                      : "Access the Maharashtra school monitoring dashboard"}
                  </p>
                </header>

                {resetMode ? (
                  <>
                    <div className="lp-form-group">
                      <label htmlFor="reset-email">Email</label>
                      <div className="lp-field">
                        <svg className="lp-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="5" width="18" height="14" rx="2" />
                          <path d="m3 7 9 6 9-6" />
                        </svg>
                        <input
                          id="reset-email"
                          type="email"
                          placeholder="your.email@school.gov.in"
                          value={resetEmail}
                          onChange={(e) => setResetEmail(e.target.value)}
                          autoComplete="email"
                          required
                        />
                        <span aria-hidden="true" />
                      </div>
                    </div>

                    <button className="lp-btn lp-btn-primary" type="submit" disabled={loading}>
                      {loading ? "Sending..." : "Send Reset Link"}
                    </button>

                    <button
                      type="button"
                      className="lp-btn lp-btn-ghost"
                      onClick={() => setResetMode(false)}
                    >
                      Back to Sign In
                    </button>
                  </>
                ) : (
                  <>
                    <div className="lp-form-group">
                      <label htmlFor="email">Email</label>
                      <div className="lp-field">
                        <svg className="lp-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="5" width="18" height="14" rx="2" />
                          <path d="m3 7 9 6 9-6" />
                        </svg>
                        <input
                          id="email"
                          type="email"
                          placeholder="your.email@school.gov.in"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          autoComplete="email"
                          required
                          data-testid="login-email"
                        />
                        <span aria-hidden="true" />
                      </div>
                    </div>

                    <div className="lp-form-group">
                      <label htmlFor="password">Password</label>
                      <div className="lp-field">
                        <svg className="lp-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="4" y="11" width="16" height="10" rx="2" />
                          <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                        </svg>
                        <input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          autoComplete="current-password"
                          required
                          data-testid="login-password"
                        />
                        <button
                          type="button"
                          className="lp-show-password"
                          onClick={() => setShowPassword((v) => !v)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          <svg className="lp-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            {showPassword ? (
                              <>
                                <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                                <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                                <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                                <line x1="2" x2="22" y1="2" y2="22" />
                              </>
                            ) : (
                              <>
                                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                                <circle cx="12" cy="12" r="3" />
                              </>
                            )}
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div className="lp-form-row">
                      <label className="lp-checkbox-label" htmlFor="remember">
                        <input
                          id="remember"
                          type="checkbox"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                        />
                        Remember me
                      </label>
                      <button type="button" className="lp-forgot-link" onClick={() => setResetMode(true)}>
                        Forgot password?
                      </button>
                    </div>

                    <button className="lp-btn lp-btn-primary" type="submit" disabled={loading} data-testid="login-submit">
                      <svg className="lp-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                        <path d="m10 17 5-5-5-5" />
                        <path d="M15 12H3" />
                      </svg>
                      {loading ? "Signing in..." : "Sign In"}
                    </button>

                    <button type="button" className="lp-btn lp-btn-secondary" onClick={fillDemoCredentials}>
                      <svg className="lp-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10 2v7.31" />
                        <path d="M14 9.3V2" />
                        <path d="M8.5 2h7" />
                        <path d="M14 9.3a6.5 6.5 0 1 1-4 0" />
                      </svg>
                      Use demo credentials
                    </button>

                    <div className="lp-demo-box" aria-label="Demo credentials">
                      <span>Demo credentials for testing</span>
                      <b>
                        {DEMO_EMAIL}&nbsp;&nbsp; / &nbsp;&nbsp;{DEMO_PASSWORD}
                      </b>
                    </div>

                    <div className="lp-secure-note">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
                        <path d="m9 12 2 2 4-4" />
                      </svg>
                      Secure access • Data is encrypted and protected
                    </div>
                  </>
                )}
              </form>
            </section>
          </div>
        </main>

        <footer className="lp-footer">
          <div className="lp-container lp-footer-inner">
            <div className="lp-gov-brand">
              <div className="lp-gov-emblem" aria-hidden="true">
                <svg width="27" height="27" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 3v18" />
                  <path d="M8 7h8" />
                  <path d="M7 11h10" />
                  <path d="M6 15h12" />
                  <path d="M5 21h14" />
                  <path d="M9 3h6" />
                </svg>
              </div>
              <div>
                <b>Government of Maharashtra</b>
                <span>School Education &amp; Sports Department</span>
              </div>
            </div>

            <div className="lp-footer-links">
              <span>🔒 © 2025 Government of Maharashtra • All rights reserved</span>
              <button type="button">Help &amp; Support</button>
              <button type="button">Privacy Policy</button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default LoginPage;
